import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import * as SecureStore from 'expo-secure-store';
import * as AppleAuthentication from 'expo-apple-authentication';
import * as Crypto from 'expo-crypto';
import { getSetCookie } from '@better-auth/expo/client';
import { signInWithCredential, OAuthCredential, OAuthProvider, signInWithPopup } from 'firebase/auth';
import { authClient } from '../lib/auth-client';
import { firebaseAuth } from '../lib/firebase';
import { useAuthStore, AuthUser } from '../stores/authStore';
import { useRoleStore } from '../stores/roleStore';
import { usersApi } from '../api/users';
import { paymentsApi } from '../api/payments';
import { QUERY_KEYS, NEON_AUTH_URL, API_BASE_URL } from '../utils/constants';

async function checkPendingVerification(setPendingVerificationPaymentId: (id: string | null) => void) {
  try {
    const pending = await paymentsApi.getPendingVerification();
    if (pending.payment_id) {
      setPendingVerificationPaymentId(pending.payment_id);
    } else {
      setPendingVerificationPaymentId(null);
    }
  } catch {
    setPendingVerificationPaymentId(null);
  }
}

const COOKIE_STORE_KEY = 'cbjj_cookie';
const SESSION_TOKEN_KEY = 'cbjj_session_token';
const AUTH_PROVIDER_KEY = 'cbjj_auth_provider';
const isWeb = Platform.OS === 'web';

/**
 * Build a callback URL for auth flows.
 * For web: uses window.location.origin
 * For native with HTTP(S) scheme: uses Linking.createURL directly
 * For native with non-HTTP scheme (exp://, cbjj://): routes through API proxy
 */
function buildAuthCallbackURL(path: string = 'auth/callback'): string {
  const deepLinkURL = Linking.createURL(path);

  // On web, use standard web URL
  if (isWeb) {
    return `${window.location.origin}/${path}`;
  }

  // If deep link already uses http/https, use it directly
  if (deepLinkURL.startsWith('http://') || deepLinkURL.startsWith('https://')) {
    return deepLinkURL;
  }

  // For non-HTTP schemes (exp:// for Expo Go, cbjj:// for production),
  // route through the API proxy so Neon Auth can callback to HTTP/HTTPS
  const apiBase = API_BASE_URL.replace('/api/v1', ''); // Get base API URL without /api/v1
  const encodedTarget = encodeURIComponent(deepLinkURL);
  return `${apiBase}/api/v1/users/auth/callback?target=${encodedTarget}`;
}

// Web-safe storage helpers (SecureStore is native-only)
const storage = {
  getItem: (key: string): string | null => {
    if (isWeb) return localStorage.getItem(key);
    return SecureStore.getItem(key);
  },
  setItem: (key: string, value: string): void => {
    if (isWeb) localStorage.setItem(key, value);
    else SecureStore.setItem(key, value);
  },
  removeItem: (key: string): void => {
    if (isWeb) localStorage.removeItem(key);
    else { try { SecureStore.deleteItemAsync(key); } catch {} }
  },
};

// ─── helpers ────────────────────────────────────────────────────────

/** Get a Neon Auth JWT token for the current session. */
async function getNeonJwt(): Promise<string> {
  const headers: Record<string, string> = {};
  // On native, pull the session cookie managed by the expoClient plugin.
  // This is the cookie populated by authClient.signUp.email / signIn.email /
  // emailOtp.verifyEmail — NOT the custom cbjj_cookie store (which is only
  // written by our manual Google SSO flow).
  if (!isWeb) {
    // The /token endpoint authenticates via the session cookie, NOT a Bearer
    // token. The expoClient plugin persists the Set-Cookie from signIn.email
    // into its SecureStore-backed cookie jar — use that as the primary auth.
    let cookieStr = '';
    try {
      const expoCookie = (authClient as any).getCookie?.();
      if (expoCookie) cookieStr = expoCookie;
    } catch {}
    if (!cookieStr) cookieStr = getStoredCookieString();
    if (cookieStr) {
      headers['Cookie'] = cookieStr;
    } else {
      // Fallback: send the session token as Bearer (used by OTP verify flow
      // where the cookie store may not be populated yet).
      const sessionToken = storage.getItem(SESSION_TOKEN_KEY);
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
    }
  }
  const apiOrigin = API_BASE_URL.replace('/api/v1', '');
  const res = await fetch(`${NEON_AUTH_URL}/token`, {
    method: 'GET',
    credentials: isWeb ? 'include' : 'omit',
    headers: { ...headers, Origin: apiOrigin },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    console.error('[getNeonJwt] /token failed', res.status, body);
    throw new Error(`Failed to get Neon JWT: ${res.status}`);
  }
  const data = await res.json();
  if (!data.token) throw new Error('No token in /token response');
  return data.token;
}

export async function syncWithBackend(opts?: {
  role?: string;
  first_name?: string;
  last_name?: string;
  dob?: string;
  terms_accepted?: boolean;
  weight_kg?: number | null;
  height_cm?: number | null;
  measurement_unit?: 'metric' | 'imperial';
  invite_token?: string;
}) {
  const jwt = await getNeonJwt();
  try {
    return await usersApi.authSync(jwt, opts);
  } catch (e: any) {
    console.error(
      '[syncWithBackend] authSync failed:',
      e?.response?.status,
      e?.response?.data,
      e?.message,
    );
    throw e;
  }
}

// ─── Discriminated result types ──────────────────────────────────────
export type LoginResult =
  | { emailVerified: true; access_token: string; user: AuthUser; is_new_user: boolean }
  | { emailVerified: false; email: string };

export type RegisterResult =
  | { emailVerified: true; email: string; role: string; first_name: string; last_name: string }
  | { emailVerified: false; email: string; role: string; first_name: string; last_name: string };

// ─── Email + Password login ─────────────────────────────────────────
export function useLogin() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const setPendingVerificationPaymentId = useAuthStore((s) => s.setPendingVerificationPaymentId);
  const setActiveRole = useRoleStore((s) => s.setActiveRole);

  return useMutation({
    mutationFn: async (data: { email: string; password: string }): Promise<LoginResult> => {
      const result = await authClient.signIn.email({
        email: data.email,
        password: data.password,
      });
      if (result.error) {
        // If verification is required, signIn fails — treat as unverified
        const msg = (result.error.message || '').toLowerCase();
        if (msg.includes('verify') || msg.includes('not verified') || msg.includes('email not')) {
          return { emailVerified: false, email: data.email };
        }
        throw new Error(result.error.message);
      }
      // Persist session token from body (native) for Bearer-auth fallback
      const sessionToken = (result.data as any)?.token;
      if (sessionToken && !isWeb) {
        storage.setItem(SESSION_TOKEN_KEY, sessionToken);
      }
      // Check emailVerified from the Better Auth session user
      const emailVerified = (result.data?.user as any)?.emailVerified ?? false;
      if (!emailVerified) {
        return { emailVerified: false, email: data.email };
      }
      const sync = await syncWithBackend();
      storage.setItem(AUTH_PROVIDER_KEY, 'neon');
      return { ...sync, emailVerified: true };
    },
    onSuccess: async (response) => {
      if (!response.emailVerified) return; // caller must navigate to verification
      setAuth(response.access_token, response.user);
      setActiveRole(response.user.role);

      // Check membership status
      try {
        const membershipCheck = await paymentsApi.getActiveMembership();
        setNeedsSubscription(!membershipCheck.has_active_membership);
      } catch {
        setNeedsSubscription(true);
      }
      await checkPendingVerification(setPendingVerificationPaymentId);
    },
  });
}

// ─── Email + Password register (creates Better Auth user) ────────────
export function useRegister() {
  return useMutation({
    mutationFn: async (data: {
      email: string;
      password: string;
      name: string;
      role: string;
      first_name: string;
      last_name: string;
    }): Promise<RegisterResult> => {
      // Build callback URL with proxy support for non-HTTP schemes
      const callbackURL = buildAuthCallbackURL('auth/callback');
      let result: any;
      try {
        result = await authClient.signUp.email({
          email: data.email,
          password: data.password,
          name: data.name,
          callbackURL,
        });
      } catch (e: any) {
        // Network/transport failure — Better Auth did not return a structured error
        console.error('[Register] signUp.email threw:', e?.message || e);
        throw new Error(
          `Network error reaching auth server (${NEON_AUTH_URL}): ${e?.message || e}`,
        );
      }
      if (result?.error) {
        const err = result.error;
        console.error('[Register] signUp.email returned error:', err, '{callbackURL:', callbackURL, '}');
        const msg =
          err.message ||
          err.statusText ||
          err.code ||
          (typeof err === 'string' ? err : JSON.stringify(err)) ||
          'Unknown auth error';
        const error = new Error(msg);
        (error as any).code = err.code;
        throw error;
      }
      // Check if email verification is required
      const emailVerified = (result.data?.user as any)?.emailVerified ?? false;
      return {
        emailVerified,
        email: data.email,
        role: data.role,
        first_name: data.first_name,
        last_name: data.last_name,
      };
    },
    onError: (error: any) => {
      console.error('[Register] mutation onError:', error?.message || error, error);
    },
  });
}

// ─── Complete registration: sync the new Better Auth user with backend ─
export function useCompleteRegistration() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const setPendingVerificationPaymentId = useAuthStore((s) => s.setPendingVerificationPaymentId);
  const setActiveRole = useRoleStore((s) => s.setActiveRole);
  const pending_invite_token = useAuthStore((s) => s.pending_invite_token);
  const setPendingInviteToken = useAuthStore((s) => s.setPendingInviteToken);

  return useMutation({
    mutationFn: async (opts: { role: string; first_name: string; last_name: string; dob?: string; terms_accepted?: boolean; weight_kg?: number | null; height_cm?: number | null; measurement_unit?: 'metric' | 'imperial' }) =>
      syncWithBackend({ ...opts, invite_token: pending_invite_token || undefined }),
    onError: (error: any) => {
      console.error(
        '[CompleteRegistration] failed:',
        error?.response?.status,
        error?.response?.data,
        error?.message,
      );
    },
    onSuccess: async (response) => {
      // Called after email verification, so emailVerified is true
      const needsCompletion =
        !response.user.dob ||
        !response.user.terms_accepted_at;
      setAuth(response.access_token, response.user, needsCompletion, true);
      setActiveRole(response.user.role);

      // Clear the pending invite token after successful sync
      if (pending_invite_token) {
        setPendingInviteToken(null);
      }

      // New users always need subscription; existing users check membership
      if (needsCompletion) {
        setNeedsSubscription(true);
      } else {
        try {
          const membershipCheck = await paymentsApi.getActiveMembership();
          setNeedsSubscription(!membershipCheck.has_active_membership);
        } catch {
          setNeedsSubscription(true);
        }
      }
      await checkPendingVerification(setPendingVerificationPaymentId);
    },
  });
}

// ─── Google SSO ──────────────────────────────────────────────────────

/** Read the stored cookie JSON from storage and build a cookie header string. */
function getStoredCookieString(): string {
  const raw = storage.getItem(COOKIE_STORE_KEY) || '{}';
  try {
    const parsed = JSON.parse(raw);
    return Object.entries(parsed).reduce((acc: string, [key, val]: [string, any]) => {
      if (val.expires && new Date(val.expires) < new Date()) return acc;
      return acc ? `${acc}; ${key}=${val.value}` : `${key}=${val.value}`;
    }, '');
  } catch {
    return '';
  }
}

/** Merge a set-cookie header into the persisted cookie store. */
function persistCookies(setCookieHeader: string) {
  const prev = storage.getItem(COOKIE_STORE_KEY) ?? undefined;
  const merged = getSetCookie(setCookieHeader, prev);
  storage.setItem(COOKIE_STORE_KEY, merged);
}

export function useGoogleSignIn() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const setPendingVerificationPaymentId = useAuthStore((s) => s.setPendingVerificationPaymentId);
  const setActiveRole = useRoleStore((s) => s.setActiveRole);
  const pending_invite_token = useAuthStore((s) => s.pending_invite_token);
  const setPendingInviteToken = useAuthStore((s) => s.setPendingInviteToken);

  return useMutation({
    mutationFn: async () => {
      console.log('[GoogleSignIn] Starting... platform:', Platform.OS);

      // WEB: Redirect to Google OAuth — AuthCallbackScreen processes the return
      if (isWeb) {
        console.log('[GoogleSignIn] Web platform: fetching OAuth URL...');
        const callbackURL = `${window.location.origin}/auth/callback`;
        const res = await fetch(`${NEON_AUTH_URL}/sign-in/social`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            provider: 'google',
            callbackURL,
            newUserCallbackURL: callbackURL,
            errorCallbackURL: callbackURL,
          }),
        });
        const data = await res.json();
        if (!data.url) throw new Error('Failed to get Google sign-in URL');
        console.log('[GoogleSignIn] Redirecting to Google OAuth...');
        window.location.href = data.url;
        // Redirect happens - code below never runs
        return new Promise(() => {});
      }

      // NATIVE: Branding proxy so the iOS/Android sign-in dialog shows our
      // domain (e.g. api.cbjjvn.com) instead of neon.tech.
      //
      // Step 1 — App POSTs to Neon Auth /sign-in/social directly (same as the
      // original working flow). This ensures the session challenge cookie is
      // bound to the app's context, not the backend's. The app persists the
      // cookie for the later /get-session call.
      //
      // Step 2 — App opens a backend redirect URL in the in-app browser. The
      // dialog shows the backend's domain. The backend 302-redirects to the
      // Neon Auth init URL. The browser follows to neon.tech → Google → back.
      const callbackURL = buildAuthCallbackURL('auth/callback');
      const deepLinkCallbackURL = Linking.createURL('auth/callback');
      const expoOrigin = Linking.createURL('', { scheme: 'cbjj' });
      const apiBase = API_BASE_URL.replace('/api/v1', '');
      const apiOrigin = apiBase;

      const existingCookie = getStoredCookieString();

      console.log('[GoogleSignIn] Step 1: POST to Neon Auth /sign-in/social');
      const initRes = await fetch(`${NEON_AUTH_URL}/sign-in/social`, {
        method: 'POST',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          'expo-origin': expoOrigin,
          'Origin': apiOrigin,
          'x-skip-oauth-proxy': 'true',
          ...(existingCookie ? { cookie: existingCookie } : {}),
        },
        body: JSON.stringify({
          provider: 'google',
          callbackURL,
          newUserCallbackURL: callbackURL,
          errorCallbackURL: callbackURL,
        }),
      });

      // Persist any cookies returned (notably the session challenge cookie).
      const initSetCookie = initRes.headers.get('set-cookie');
      if (initSetCookie) persistCookies(initSetCookie);

      const initData = await initRes.json();
      if (!initData.url) {
        throw new Error(initData.message || 'Failed to get Google sign-in URL');
      }

      // Step 2 — Open the backend redirect URL in the in-app browser.
      // The browser shows the backend's domain in the sign-in dialog, then
      // the backend 302-redirects to the Neon Auth init URL.
      const redirectUrl =
        `${apiBase}/api/v1/users/auth/google-redirect` +
        `?url=${encodeURIComponent(initData.url)}`;

      console.log('[GoogleSignIn] Step 2: Opening browser via backend redirect:', redirectUrl);
      if (Platform.OS === 'android') {
        try { WebBrowser.dismissAuthSession(); } catch {}
      }
      const browserResult = await WebBrowser.openAuthSessionAsync(redirectUrl, deepLinkCallbackURL);
      if (browserResult.type !== 'success') {
        throw new Error('Google sign-in was cancelled');
      }

      // Step 3 — Extract the session verifier and exchange for session
      console.log('[GoogleSignIn] Step 3: Processing redirect URL');
      const resultUrl = new URL(browserResult.url);
      const verifier = resultUrl.searchParams.get('neon_auth_session_verifier');
      const cookieParam = resultUrl.searchParams.get('cookie');

      if (verifier) {
        console.log('[GoogleSignIn] Exchanging verifier for session...');
        const sessionCookie = getStoredCookieString();
        const sessionRes = await fetch(
          `${NEON_AUTH_URL}/get-session?neon_auth_session_verifier=${encodeURIComponent(verifier)}`,
          {
            method: 'GET',
            credentials: 'omit',
            headers: {
              'expo-origin': expoOrigin,
              'Origin': apiOrigin,
              'x-skip-oauth-proxy': 'true',
              ...(sessionCookie ? { cookie: sessionCookie } : {}),
            },
          },
        );
        if (!sessionRes.ok) {
          const body = await sessionRes.text().catch(() => '');
          console.error('[GoogleSignIn] /get-session failed:', sessionRes.status, body);
          throw new Error(`Failed to exchange session verifier (${sessionRes.status}): ${body}`);
        }

        const sessionSetCookie = sessionRes.headers.get('set-cookie');
        if (sessionSetCookie) persistCookies(sessionSetCookie);
      } else if (cookieParam) {
        persistCookies(cookieParam);
      } else {
        throw new Error('No session data received after sign-in');
      }

      // Step 4 — Get a Neon JWT and sync with backend (include invite token if present)
      console.log('[GoogleSignIn] Step 4: Getting JWT and syncing with backend...');
      const jwt = await getNeonJwt();
      storage.setItem(AUTH_PROVIDER_KEY, 'neon');
      return usersApi.authSync(jwt, pending_invite_token ? { invite_token: pending_invite_token } : undefined);
    },
    onError: (error) => {
      console.error('[GoogleSignIn] Error:', error);
    },
    onSuccess: async (response) => {
      // Google OAuth users are ALWAYS treated as verified — Google has already
      // validated the email. We deliberately do NOT consult the Better Auth
      // session flag here: reading it right after account creation can race the
      // provider's own bookkeeping and return a stale `false`, which used to
      // strand brand-new Google users on the email-verification (OTP) screen.
      const emailVerified = true;

      // Check if user needs to complete profile (SSO users won't have dob/terms)
      const needsCompletion =
        !response.user.dob ||
        !response.user.terms_accepted_at;

      // Always set auth (so JWT is available), but flag onboarding if needed
      setAuth(response.access_token, response.user, needsCompletion, emailVerified);
      setActiveRole(response.user.role);

      // Clear the pending invite token after successful sync (sub-user invite flow)
      if (pending_invite_token) {
        setPendingInviteToken(null);
      }

      // Check membership status (new users always need subscription)
      if (needsCompletion) {
        setNeedsSubscription(true);
      } else {
        try {
          const membershipCheck = await paymentsApi.getActiveMembership();
          setNeedsSubscription(!membershipCheck.has_active_membership);
        } catch {
          setNeedsSubscription(true);
        }
      }
      await checkPendingVerification(setPendingVerificationPaymentId);
    },
  });
}

// ─── Apple SSO (via Firebase Auth) ────────────────────────────────────

/**
 * Generate a random nonce string (hex-encoded) for Apple Sign In.
 * Firebase requires the SHA256 hash of this nonce to be passed to Apple,
 * and the raw nonce to be included in the OAuth credential.
 */
function generateNonce(length: number = 32): string {
  const chars = '0123456789abcdef';
  const bytes = Crypto.getRandomValues(new Uint8Array(length));
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars[bytes[i] % chars.length];
  }
  return result;
}

/** SHA-256 hash a string and return hex output (uses expo-crypto). */
async function sha256Hex(input: string): Promise<string> {
  const hash = await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, input, {
    encoding: Crypto.CryptoEncoding.HEX,
  });
  return hash;
}

export function useAppleSignIn() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const setNeedsSubscription = useAuthStore((s) => s.setNeedsSubscription);
  const setPendingVerificationPaymentId = useAuthStore((s) => s.setPendingVerificationPaymentId);
  const setActiveRole = useRoleStore((s) => s.setActiveRole);
  const pending_invite_token = useAuthStore((s) => s.pending_invite_token);
  const setPendingInviteToken = useAuthStore((s) => s.setPendingInviteToken);

  return useMutation({
    mutationFn: async (): Promise<any> => {
      console.log('[AppleSignIn] Starting... platform:', Platform.OS);

      if (isWeb) {
        // ── Web: use Firebase popup ────────────────────────────────────
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');
        const cred = await signInWithPopup(firebaseAuth, provider);
        const idToken = await cred.user.getIdToken();
        storage.setItem(AUTH_PROVIDER_KEY, 'firebase');
        return usersApi.authSync(idToken, pending_invite_token ? { invite_token: pending_invite_token } : undefined);
      }

      // ── Native (iOS): use expo-apple-authentication + Firebase credential ──
      const rawNonce = generateNonce();
      const hashedNonce = await sha256Hex(rawNonce);

      const appleCredential = await AppleAuthentication.signInAsync({
        requestedScopes: [
          AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
          AppleAuthentication.AppleAuthenticationScope.EMAIL,
        ],
        nonce: hashedNonce,
      });

      const { identityToken, authorizationCode, fullName } = appleCredential;
      if (!identityToken) {
        throw new Error('Apple Sign In failed: no identity token returned');
      }

      // Build the Firebase OAuth credential for Apple.
      // Firebase expects the raw (unhashed) nonce used to verify the Apple ID token.
      const provider = new OAuthProvider('apple.com');
      const credential = provider.credential({
        idToken: identityToken,
        rawNonce: rawNonce,
      });
      if (!credential) {
        throw new Error('Apple Sign In failed: could not build Firebase credential');
      }

      const userCred = await signInWithCredential(firebaseAuth, credential);

      // Apple only provides the full name on the FIRST sign-in. If the Firebase
      // user has no display name but Apple gave us one, update the Firebase profile.
      if (fullName && !userCred.user.displayName) {
        const given = fullName.givenName?.trim() || '';
        const family = fullName.familyName?.trim() || '';
        const displayName = [given, family].filter(Boolean).join(' ').trim();
        if (displayName) {
          try {
            const { updateProfile } = await import('firebase/auth');
            await updateProfile(userCred.user, { displayName });
          } catch (e) {
            console.warn('[AppleSignIn] Could not update Firebase display name:', e);
          }
        }
      }

      const idToken = await userCred.user.getIdToken();
      storage.setItem(AUTH_PROVIDER_KEY, 'firebase');
      return usersApi.authSync(idToken, pending_invite_token ? { invite_token: pending_invite_token } : undefined);
    },
    onError: (error) => {
      console.error('[AppleSignIn] Error:', error);
    },
    onSuccess: async (response) => {
      // Apple Sign In users are auto-verified (Apple validates the email)
      const emailVerified = true;

      // Apple may not provide a name (only sent on first sign-in, or user
      // may hide it). Flag for profile completion if name is missing.
      const needsCompletion =
        !response.user.dob ||
        !response.user.terms_accepted_at ||
        !response.user.first_name ||
        !response.user.last_name;

      setAuth(response.access_token, response.user, needsCompletion, emailVerified);
      setActiveRole(response.user.role);

      if (pending_invite_token) {
        setPendingInviteToken(null);
      }

      if (needsCompletion) {
        setNeedsSubscription(true);
      } else {
        try {
          const membershipCheck = await paymentsApi.getActiveMembership();
          setNeedsSubscription(!membershipCheck.has_active_membership);
        } catch {
          setNeedsSubscription(true);
        }
      }
      await checkPendingVerification(setPendingVerificationPaymentId);
    },
  });
}

// ─── Forgot password ────────────────────────────────────────────────
export function useForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      const redirectTo = buildAuthCallbackURL('reset-password');
      const result = await authClient.requestPasswordReset({ email, redirectTo });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}

// ─── Reset password (after clicking link in email) ──────────────────
export function useResetPassword() {
  return useMutation({
    mutationFn: async (data: { newPassword: string; token: string }) => {
      const result = await authClient.resetPassword({ newPassword: data.newPassword, token: data.token });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}

// ─── Send email verification OTP ────────────────────────────────────
export function useSendVerificationEmail() {
  return useMutation({
    mutationFn: async (email: string) => {
      const callbackURL = buildAuthCallbackURL('auth/callback');
      const result = await authClient.sendVerificationEmail({ email, callbackURL });
      if (result.error) throw new Error(result.error.message);
      return result.data;
    },
  });
}

// ─── Verify email with OTP code ──────────────────────────────────────
export function useVerifyEmail() {
  return useMutation({
    mutationFn: async (data: { email: string; otp: string }) => {
      const ba = authClient as any;
      if (!ba.emailOtp?.verifyEmail) {
        throw new Error('Email OTP verification is not available. Please use the verification link in your email instead.');
      }
      // On native, call the endpoint directly so we can capture the
      // Set-Cookie and set-auth-token response headers for session persistence.
      // The authClient wrapper does not expose raw response headers reliably.
      if (!isWeb) {
        // Raw fetch (not authClient) so we can read Set-Cookie / set-auth-token
        // response headers for manual session persistence — the SDK swallows them.
        // customFetchImpl only applies to authClient calls, not bare fetch(),
        // so Origin headers must be set explicitly here.
        const expoOrigin = Linking.createURL('', { scheme: 'cbjj' });
        const apiOrigin = API_BASE_URL.replace('/api/v1', '');
        const rawRes = await fetch(`${NEON_AUTH_URL}/email-otp/verify-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'expo-origin': expoOrigin,
            'Origin': apiOrigin,
            'x-skip-oauth-proxy': 'true',
          },
          body: JSON.stringify({ email: data.email, otp: data.otp }),
        });
        const bodyText = await rawRes.text();
        if (!rawRes.ok) {
          let msg = 'Verification failed';
          try { msg = JSON.parse(bodyText)?.message || msg; } catch {}
          throw new Error(msg);
        }
        const body = bodyText ? JSON.parse(bodyText) : {};
        const rawSetCookie = rawRes.headers.get('set-cookie');
        const rawSetAuthToken = rawRes.headers.get('set-auth-token');
        if (rawSetCookie) persistCookies(rawSetCookie);
        const tokenToStore = rawSetAuthToken || body?.token;
        if (tokenToStore) storage.setItem(SESSION_TOKEN_KEY, tokenToStore);
        return body;
      }

      // Web path — use authClient normally (browser cookie jar handles session)
      const result = await ba.emailOtp.verifyEmail({
        email: data.email,
        otp: data.otp,
      });
      if (result?.error) {
        throw new Error(result.error.message || result.error.code || 'Verification failed');
      }
      return result.data;
    },
  });
}

// ─── Update user profile ────────────────────────────────────────────
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const updateUser = useAuthStore((s) => s.updateUser);

  return useMutation({
    mutationFn: async (data: { first_name?: string; last_name?: string; dob?: string | null; terms_accepted?: boolean; weight_kg?: number | null; height_cm?: number | null; measurement_unit?: string | null }) => {
      if (data.first_name && data.last_name) {
        await authClient.updateUser({ name: `${data.first_name} ${data.last_name}` });
      }
      return usersApi.updateMe(data);
    },
    onSuccess: (responseData) => {
      updateUser(responseData);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.userMe] });
    },
  });
}

// ─── Get current user (backend) ─────────────────────────────────────
export function useCurrentUser() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: [QUERY_KEYS.userMe],
    queryFn: usersApi.getMe,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

// ─── Logout ─────────────────────────────────────────────────────────
export function useLogout() {
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const queryClient = useQueryClient();

  return async () => {
    // Sign out from whichever auth provider is active.
    // If the user logged in via Apple (Firebase), sign out from Firebase;
    // always sign out from Neon/Better Auth as well (it's a no-op if no session).
    const provider = storage.getItem(AUTH_PROVIDER_KEY);
    if (provider === 'firebase') {
      try {
        const { signOut: firebaseSignOut } = await import('firebase/auth');
        await firebaseSignOut(firebaseAuth);
      } catch {}
    }
    try { await authClient.signOut(); } catch {}
    storage.removeItem(SESSION_TOKEN_KEY);
    storage.removeItem(COOKIE_STORE_KEY);
    storage.removeItem(AUTH_PROVIDER_KEY);
    clearAuth();
    queryClient.clear();
  };
}

// ─── Handle OAuth callback (web only) ───────────────────────────────
// NOTE: This hook is DEPRECATED. OAuth callback is now handled by AuthCallbackScreen.
// Keeping the export for backward compatibility but it's a no-op.
export function useHandleOAuthCallback() {
  // No-op — callback handling moved to AuthCallbackScreen
}
